import React from 'react';
import { StrikethroughValues } from 'types/StrikethroughValues';
import { newrelic } from 'helpers/reporting/newrelic';
import { ProductType } from 'features/pdp/types/event';
import { formatPrice } from 'helpers/currency';
import { StrikethroughSpan } from 'components/Strikethrough/styled';

const REGEX_PRICE_CAPTURE = /\$(\d|\.)+/;

export enum DiscountType {
  BY_PERCENT = 'by_percent',
  BY_FIXED = 'by_fixed',
  GIFT_CARD_SKU = 'email-giftcard'
}

export const getDiscountPriceByPercent = (product: ProductType, price: number): number => {
  const percentDiscount = product?.strikethroughValues?.discountType === DiscountType.BY_PERCENT
    && product.strikethroughValues.discountAmount;
  return percentDiscount ? strikethroughPriceByPercent(price, percentDiscount) : undefined;
};

export const getStrikethroughPrice = (
  strikethroughValues: StrikethroughValues,
  productPrice: number,
  productSku: string
): number | null => {
  const strikethroughPrice: number | null = null;

  if (strikethroughValues && productSku !== DiscountType.GIFT_CARD_SKU) {
    const { discountType, discountAmount } = strikethroughValues;

    if (discountType === DiscountType.BY_PERCENT) {
      if (discountAmount > 100) {
        reportInvalidDiscount(
          'DISCOUNT_BY_PERCENT_GREATER_THAN_100',
          invalidStrikethroughAndPriceValues(strikethroughValues, productPrice)
        );

        return productPrice;
      }

      return strikethroughPriceByPercent(productPrice, discountAmount);
    }

    if (discountType === DiscountType.BY_FIXED) {
      if (discountAmount > productPrice) {
        reportInvalidDiscount(
          'FIXED_DISCOUNT_AMOUNT_GREATER_THAN_PRODUCT_PRICE',
          invalidStrikethroughAndPriceValues(strikethroughValues, productPrice)
        );

        return productPrice;
      }

      return strikethroughPriceByFixed(productPrice, discountAmount);
    }
  }

  return strikethroughPrice;
};

export const strikethroughPriceByPercent = (productPrice: number, discountAmount: number): number => {
  const discount = productPrice * (discountAmount / 100);
  return productPrice - discount;
};

const strikethroughPriceByFixed = (productPrice: number, discountAmount: number): number =>
  productPrice - discountAmount;

const reportInvalidDiscount = (type: string, values: object): void => {
  newrelic('addPageAction')(type, values);
};

const invalidStrikethroughAndPriceValues = (strikethroughValues: StrikethroughValues, price: number): object => ({
  strikethroughValues,
  price
});

export const renderStrikethroughPricing = (price: number, product?: ProductType): JSX.Element => {
  const discountedPrice = getDiscountPriceByPercent(product, price);
  const strikethroughPrice = discountedPrice && formatPrice(price);
  const currentPrice = formatPrice(discountedPrice ?? price);
  return (
    <>
      {currentPrice}
      {strikethroughPrice &&
        <StrikethroughSpan className="strikethrough-price">
          {strikethroughPrice}
        </StrikethroughSpan>
      }
    </>
  );
};

/**
 * updateStrikethroughPriceInString
 * 
 * Takes a string containing the price of an option, and replaces it with the discounted price.
 * Only applies percent discounts since we cannot accurately calculate partial fixed discounts for options.
 * Returns an array of React Elements or strings, in order to individually style the strikethrough price.
 * 
 * If there is no price found in the string, returns the original string.
 * 
 * @param string Target string containing product/option price
 * @param product Product object
 * @returns 
 */
export const updateStrikethroughPriceInString = (string: string, product: ProductType, price?: number): Array<JSX.Element | string> => {
  const priceString = string?.match(REGEX_PRICE_CAPTURE)?.[0];

  if (!priceString) { return [string]; }

  const priceSplit: Array<JSX.Element | string> = string.split(priceString);
  const basePrice = price || parseFloat(priceString.replace(/[^\d.]/g, ''));

  const strikethroughPricing = renderStrikethroughPricing(basePrice, product);
  priceSplit.splice(1, 0 , strikethroughPricing);
  return priceSplit;
};
              

